import { defaultCurrency } from "@zapier/currency";
import { css } from "@emotion/react";
import { SkeletonBlock } from "@zapier/design-system";

import { useQuery } from "@apollo/client";

import { useEffect, Fragment, ReactNode } from "react";
import { useDisplayCurrency } from "@/hooks";
import { usePricingPageContext } from "@/pages/pricing";
import { IntervalDropdown } from "@/components/PricingPage/IntervalDropdown";
import { IntervalToggle } from "@/components/PricingPage/IntervalToggle";
import { CurrencyDropdown } from "@zapier/checkout";
import { hiddenOnMobile, visibleOnlyOnMobile } from "@/utils";
import { getAnnualDiscountPercentage } from "@/utils/getAnnualDiscountPercentage";
import { useRendererContext } from "@zapier/builder.io-kit";
import { Currency, currencyTypeName } from "@/types";
import { PLAN_CHANGE_EVENT, geoCurrencyLoadedEventData } from "@/constants";
import { useTrackingContext } from "@zapier/toolbox-tracking";
import { CurrencyChargeWarningBar } from "@/components/PricingPage/CurrencyChargeWarningBar";

const Styles = {
  root: css({
    maxWidth: "1170px",
    padding: "20px",
    margin: "0 auto",
    "@media (min-width: 640px)": {
      padding: "40px 56px 20px 56px",
    },
    "@media (min-width: 1240px)": {
      paddingTop: "70px",
    },
  }),

  currencyDropdown: css({
    minHeight: "44px",
    width: "100%",
    "@media (min-width: 640px)": {
      width: "160px",
    },
  }),

  intervalDropdown: css({
    width: "100%",
    "@media (min-width: 640px)": {
      width: "160px",
    },
  }),

  contentWrapper: (hideIntervalOptions?: boolean) =>
    css({
      "@media (min-width: 1240px)": {
        padding: "0 20px",
      },
      ...(hideIntervalOptions && {
        display: "flex",
        flexDirection: "column",
        alignItems: "end",
        gap: "20px",
        "@media (min-width: 640px)": {
          flexDirection: "row",
        },
      }),
    }),
  configWrapper: (hideIntervalOptions?: boolean) =>
    css({
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      ...(hideIntervalOptions
        ? {
            width: "100%",
            justifyContent: "end",
            "@media (min-width: 640px)": {
              width: "auto",
            },
          }
        : {
            margin: "30px 0",
            justifyContent: "space-between",
            "@media (min-width: 640px)": {
              flexDirection: "row",
            },
          }),
    }),
};

export type PricingOptionsV2Props = {
  stateKey: string;
  hideIntervalOptions?: boolean;
  children?: ReactNode;
};
export default function PricingOptionsV2({
  children,
  hideIntervalOptions,
  stateKey,
}: PricingOptionsV2Props) {
  const { trackEvent } = useTrackingContext();

  const { useStateByKey } = useRendererContext();

  const { planStacks } = usePricingPageContext();
  const [{ selectedCurrency, isAnnual = true }, setState] = useStateByKey<{
    selectedCurrency: Currency;
    isAnnual: boolean;
  }>(stateKey);

  const annualDiscountAmount = getAnnualDiscountPercentage(planStacks);
  const {
    currencyType,
    displayCurrency,
    isLoading: isLoadingCurrency,
  } = useDisplayCurrency({ countryCode: "" });

  const fallbackCurrency = displayCurrency || defaultCurrency;
  const currency = selectedCurrency || fallbackCurrency;

  const setIsAnnual = (bool: boolean) => setState({ isAnnual: bool });

  useEffect(() => {
    if (isLoadingCurrency || !displayCurrency || selectedCurrency) return;

    setState({ selectedCurrency: fallbackCurrency });

    if (currencyType === currencyTypeName.GEOLOCATED)
      trackEvent(PLAN_CHANGE_EVENT, {
        absolute_url: window.location.href,
        ...geoCurrencyLoadedEventData,
        option_selected: displayCurrency.name,
      });
  }, [
    stateKey,
    setState,
    displayCurrency,
    isLoadingCurrency,
    trackEvent,
    currencyType,
    fallbackCurrency,
    selectedCurrency,
  ]);

  const showWarningAbove =
    !isLoadingCurrency &&
    currency?.name !== defaultCurrency.name &&
    !hideIntervalOptions;
  const showWarningBelow =
    !isLoadingCurrency &&
    currency?.name !== defaultCurrency.name &&
    hideIntervalOptions;
  return (
    <div css={Styles.root}>
      <div css={Styles.contentWrapper(hideIntervalOptions)}>
        {children}
        {showWarningAbove && (
          <CurrencyChargeWarningBar
            margin="20px 0 10px 0"
            selectedCurrencyCode={currency.name}
          />
        )}
        <div css={Styles.configWrapper(hideIntervalOptions)}>
          {!hideIntervalOptions && (
            <Fragment>
              <div css={hiddenOnMobile}>
                <IntervalToggle
                  annualDiscountPercent={annualDiscountAmount}
                  isAnnual={isAnnual}
                  onChange={setIsAnnual}
                  css={hiddenOnMobile}
                />
              </div>
              <div css={[Styles.intervalDropdown, visibleOnlyOnMobile]}>
                <IntervalDropdown
                  annualDiscountPercent={annualDiscountAmount}
                  isAnnual={isAnnual}
                  onChange={setIsAnnual}
                />
              </div>
            </Fragment>
          )}
          <div css={Styles.currencyDropdown}>
            {!isLoadingCurrency ? (
              <CurrencyDropdown
                onSelectCurrency={(c) => setState({ selectedCurrency: c })}
                selectedCurrency={currency}
                useQuery={useQuery}
                css={css`
                  width: 100% !important;
                `}
              />
            ) : (
              <SkeletonBlock height="44px" width="100%" />
            )}
          </div>
        </div>
      </div>
      {showWarningBelow && (
        <CurrencyChargeWarningBar
          margin="20px 0 10px 0"
          selectedCurrencyCode={selectedCurrency.name}
        />
      )}
    </div>
  );
}
